import { useEffect, useState } from "react";
import { useI18n } from "@/lib/i18n";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, ShoppingCart, Trash2, RefreshCw, Loader2, Store, ExternalLink } from "lucide-react";

export default function ShopsPage() {
  const { t } = useI18n();
  const [shops, setShops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [importing, setImporting] = useState(null);
  const [newShop, setNewShop] = useState({ name: "", url: "", api_key: "", api_secret: "", scan_countries: [] });

  const COUNTRIES = [
    { code: "nl", key: "shops.country_nl" }, { code: "be", key: "shops.country_be" }, { code: "fr", key: "shops.country_fr" },
    { code: "de", key: "shops.country_de" }, { code: "uk", key: "shops.country_uk" }, { code: "at", key: "shops.country_at" },
    { code: "it", key: "shops.country_it" }, { code: "es", key: "shops.country_es" }, { code: "pt", key: "shops.country_pt" },
    { code: "lu", key: "shops.country_lu" }, { code: "ch", key: "shops.country_ch" }, { code: "se", key: "shops.country_se" },
    { code: "dk", key: "shops.country_dk" }, { code: "pl", key: "shops.country_pl" },
  ];

  const toggleCountry = (code) => {
    setNewShop(prev => ({
      ...prev,
      scan_countries: prev.scan_countries.includes(code)
        ? prev.scan_countries.filter(c => c !== code)
        : [...prev.scan_countries, code]
    }));
  };

  const fetchShops = () => {
    api.get("/woocommerce/shops").then((r) => setShops(r.data)).catch(console.error).finally(() => setLoading(false));
  };

  useEffect(() => { fetchShops(); }, []);

  const handleAdd = async () => {
    try {
      const shop = await api.post("/woocommerce/shops", newShop);
      toast.success(t("toast.shop_added"));
      setShowAdd(false);
      setNewShop({ name: "", url: "", api_key: "", api_secret: "", scan_countries: [] });
      fetchShops();

      // Auto-import is best-effort: the shop is already saved at this point
      setImporting(shop.data.id);
      try {
        const res = await api.post("/woocommerce/import", { shop_id: shop.data.id });
        toast.success(`${t("toast.sync_complete")}: ${res.data.imported} ${t("shops.products")}`);
      } catch {
        toast.info(t("toast.shop_added_manual"));
      } finally {
        setImporting(null);
        fetchShops();
      }
    } catch (err) { toast.error(err.response?.data?.detail || t("toast.add_failed")); }
  };

  const handleSync = async (shopId) => {
    setImporting(shopId);
    try {
      const res = await api.post("/woocommerce/import", { shop_id: shopId });
      toast.success(`${t("toast.sync_complete")}: ${res.data.imported} nieuw, ${res.data.updated} bijgewerkt`);
      fetchShops();
    } catch (err) { toast.error(err.response?.data?.detail || t("toast.sync_failed")); }
    finally { setImporting(null); }
  };

  const handleDelete = async (shopId) => {
    if (!window.confirm(t("shops.delete_confirm"))) return;
    try {
      await api.delete(`/woocommerce/shops/${shopId}`);
      toast.success(t("toast.shop_deleted"));
      fetchShops();
    } catch { toast.error(t("toast.delete_failed")); }
  };

  if (loading) return <div className="flex items-center justify-center h-64 text-muted-foreground">{t("common.loading")}</div>;

  return (
    <div className="space-y-6" data-testid="shops-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">{t("shops.title")}</h1>
          <p className="text-sm text-muted-foreground mt-1">{t("shops.subtitle")}</p>
        </div>
        <Dialog open={showAdd} onOpenChange={setShowAdd}>
          <DialogTrigger asChild>
            <Button className="bg-[#2CA8AC] hover:bg-[#248F92]" data-testid="add-shop-btn">
              <Plus className="w-4 h-4 mr-2" /> {t("shops.add")}
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>{t("shops.add_woo")}</DialogTitle></DialogHeader>
            <div className="space-y-4 py-2">
              <div className="bg-[#85D8DB]/10 rounded-lg p-4 text-sm text-[#1A3F6E] space-y-2">
                <p className="font-semibold">{t("shops.api_instructions_title")}</p>
                <ol className="list-decimal pl-4 space-y-1 text-xs text-slate-600">
                  <li>{t("shops.api_step1")}</li>
                  <li>{t("shops.api_step2")}</li>
                  <li>{t("shops.api_step3")}</li>
                  <li>{t("shops.api_step4")}</li>
                </ol>
                <p className="text-xs text-slate-500">{t("shops.api_permission_info")}</p>
              </div>
              <div><Label>{t("shops.name_label")} *</Label><Input value={newShop.name} onChange={(e) => setNewShop({...newShop, name: e.target.value})} placeholder="Mijn Webshop" className="mt-1" data-testid="shop-name-input" /></div>
              <div><Label>{t("shops.url_label")} *</Label><Input value={newShop.url} onChange={(e) => setNewShop({...newShop, url: e.target.value})} placeholder="https://mijn-webshop.be" className="mt-1" data-testid="shop-url-input" /></div>
              <div><Label>{t("shops.consumer_key")}</Label><Input value={newShop.api_key} onChange={(e) => setNewShop({...newShop, api_key: e.target.value})} placeholder="ck_..." className="mt-1 font-mono-data text-sm" data-testid="shop-key-input" /></div>
              <div><Label>{t("shops.consumer_secret")}</Label><Input type="password" value={newShop.api_secret} onChange={(e) => setNewShop({...newShop, api_secret: e.target.value})} placeholder="cs_..." className="mt-1 font-mono-data text-sm" data-testid="shop-secret-input" /></div>
              <p className="text-xs text-muted-foreground">{t("shops.keys_hint")}</p>
              <div>
                <Label className="text-sm font-semibold">{t("shops.scan_countries")}</Label>
                <p className="text-xs text-muted-foreground mb-2">{t("shops.scan_countries_hint")}</p>
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                  {COUNTRIES.map((c) => (
                    <label key={c.code} className="flex items-center gap-2 text-xs cursor-pointer hover:bg-accent/50 p-1.5 rounded">
                      <Checkbox checked={newShop.scan_countries.includes(c.code)} onCheckedChange={() => toggleCountry(c.code)} />
                      <span>{t(c.key)}</span>
                    </label>
                  ))}
                </div>
              </div>

            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowAdd(false)}>{t("shops.cancel")}</Button>
              <Button onClick={handleAdd} className="bg-blue-600" data-testid="save-shop-btn">{t("shops.add_btn")}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {shops.length === 0 ? (
        <Card className="bg-card border-border">
          <CardContent className="p-12 text-center">
            <Store className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2">{t("shops.no_shops")}</h3>
            <p className="text-muted-foreground text-sm mb-6">{t("shops.no_shops_desc")}</p>
            <Button onClick={() => setShowAdd(true)} className="bg-blue-600"><Plus className="w-4 h-4 mr-2" />{t("shops.add")}</Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {shops.map((shop) => (
            <Card key={shop.id} className="bg-card border-border hover:border-primary/30 transition-colors" data-testid={`shop-card-${shop.id}`}>
              <CardContent className="p-5 flex items-center gap-5">
                <div className="w-12 h-12 rounded-xl bg-purple-500/10 flex items-center justify-center shrink-0">
                  <ShoppingCart className="w-6 h-6 text-purple-500" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <h3 className="font-bold">{shop.name}</h3>
                    <Badge variant={shop.status === "active" ? "default" : "secondary"} className={shop.status === "active" ? "bg-emerald-500/20 text-emerald-400 border-0" : ""}>{shop.status === "active" ? t("shops.active") : shop.status}</Badge>
                  </div>
                  <div className="flex items-center gap-3 mt-1 text-sm text-muted-foreground">
                    <a href={shop.url} target="_blank" rel="noopener noreferrer" className="hover:text-primary flex items-center gap-1">{shop.url} <ExternalLink className="w-3 h-3" /></a>
                    <span className="font-mono-data">{shop.product_count || 0} {t("shops.products")}</span>
                    {shop.last_sync && <span>{t("shops.last_sync")} {new Date(shop.last_sync).toLocaleString("nl-NL")}</span>}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={() => handleSync(shop.id)} disabled={importing === shop.id} data-testid={`sync-shop-${shop.id}`}>
                    {importing === shop.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1.5" />}
                    {t("shops.sync")}
                  </Button>
                  <Button variant="ghost" size="icon" className="text-destructive" onClick={() => handleDelete(shop.id)} data-testid={`delete-shop-${shop.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
